import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const STORAGE_PREFIX = 'listParams:';

const readStored = (storageKey: string): string => {
  try {
    return sessionStorage.getItem(STORAGE_PREFIX + storageKey) || '';
  } catch {
    return '';
  }
};

const writeStored = (storageKey: string, value: string): void => {
  try {
    if (value) {
      sessionStorage.setItem(STORAGE_PREFIX + storageKey, value);
    } else {
      sessionStorage.removeItem(STORAGE_PREFIX + storageKey);
    }
  } catch {
    // Storage unavailable (private window / blocked) - the list view just isn't remembered
  }
};

/**
 * Remembers a list page's query string (filters + pagination) for the current browser tab,
 * so returning to the list through a bare URL (back button, sidebar, dashboard) restores the last view.
 * Params already in the URL (shared link, refresh) always win over the remembered ones.
 */
function usePersistedSearchParams(storageKey: string) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Computed once - the pages read it only in their useState initializers
  const [initialParams] = useState(() =>
    searchParams.toString()
      ? new URLSearchParams(searchParams)
      : new URLSearchParams(readStored(storageKey))
  );

  const persistSearchParams = useCallback((params: URLSearchParams) => {
    writeStored(storageKey, params.toString());
    setSearchParams(params, { replace: true });
  }, [storageKey, setSearchParams]);

  return { initialParams, persistSearchParams };
}

/** Forgets every remembered list view (on logout, so the next user starts with clean lists) */
export const clearPersistedSearchParams = (): void => {
  try {
    Object.keys(sessionStorage)
      .filter(key => key.startsWith(STORAGE_PREFIX))
      .forEach(key => sessionStorage.removeItem(key));
  } catch {
    // Storage unavailable - nothing was remembered
  }
};

export default usePersistedSearchParams;
